import { cx } from "@emotion/css";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import CardRoundedIcon from "@mui/icons-material/SimCardRounded";
import { Divider } from "@mui/material";
import { ExpressCheckoutElement } from "@stripe/react-stripe-js";
import { differenceInMilliseconds } from "date-fns";
import { useFormikContext } from "formik";
import useOpenClose from "hooks/useOpenClose";
import CreditCardInput from "library/Atoms/Input/CreditCardInput";
import BoxLayout from "library/Layouts/BoxLayout/BoxLayout";
import MessageLayout from "library/Layouts/MessageLayout";
import RadioGroup from "library/Molecules/RadioGroup";
import { every, isEmpty, values } from "lodash";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PaymentIcon } from "react-svg-credit-card-payment-icons";
import { convertTimestampToDate } from "utility/helpers/general";
import ExpiredSessionModal from "./modals/ExpiredSessionModal";
import styles from "./styles";

const CreditCardForm = ({ name, onComplete, onConfirm, onSelect, onRefreshSession, error, isRefreshing }) => {
  const { t } = useTranslation();

  const {
    values: {
      [name]: { cards, defaultCard, expire_at },
    },
  } = useFormikContext();

  const [cardState, setCardState] = useState({ cardNumber: false, cardCvc: false, cardExpiry: false });
  const [savedCard, setSavedCard] = useState(defaultCard);

  const [isExpiredSessionModalOpen, openExpiredSessionModal, closeExpiredSessionModal] = useOpenClose(false);

  const onSelectCard = useCallback(
    (value) => {
      setSavedCard(value);
      onSelect(value);
    },
    [onSelect]
  );

  const onCardStateChange = useCallback(
    (type, complete) => {
      onSelectCard(null);
      setCardState((prev) => ({ ...prev, [type]: complete }));
    },
    [onSelectCard]
  );

  useEffect(() => {
    onComplete(every(values(cardState), Boolean));
  }, [cardState, onComplete]);

  useEffect(() => {
    closeExpiredSessionModal();
  }, [closeExpiredSessionModal, expire_at]);

  useEffect(() => {
    if (!expire_at) return () => {};

    const timerId = setTimeout(() => {
      openExpiredSessionModal();
    }, differenceInMilliseconds(new Date(convertTimestampToDate(expire_at)), new Date()));
    return () => clearTimeout(timerId);
  }, [openExpiredSessionModal, expire_at]);

  return (
    <>
      <BoxLayout className="p-4" shadow={false} selected>
        <div className="d-flex flex-row flex-1 justify-content-between">
          <div className="text-fs-14 text-fw-bold">{t("features.creditCardForm.formTitle")}</div>

          <CreditCardRoundedIcon className={styles.smallCardIcon} fontSize="small" />
        </div>

        <CardRoundedIcon className={cx(styles.largeCardIcon, "my-4")} color="primary" />

        {cards && !isEmpty(cards) && (
          <>
            <RadioGroup
              value={savedCard}
              options={cards.map((card) => ({ value: card.id, data: card }))}
              onSelect={onSelectCard}
              renderItem={({ brand, last4, exp_month, exp_year }) => (
                <div className="d-flex flex-row justify-content-between text-fs-12">
                  <div className="d-flex flex-row gap-4 text-fw-semibold">
                    <PaymentIcon type={brand} format="logo" width={30} />
                    <span>**** {last4}</span>
                  </div>

                  <span className="text-color-grey400">
                    {exp_month}/{exp_year}
                  </span>
                </div>
              )}
            />

            <Divider className="my-3">{t("common.forms.separatorLabel")}</Divider>
          </>
        )}

        <CreditCardInput
          className="mb-4"
          label={t("features.creditCardForm.cardNumberLabel")}
          onStateChange={onCardStateChange}
          required
        />

        <div className="d-flex flex-md-row flex-column col-12 pt-2 px-0">
          <CreditCardInput
            className="col-md-6 col-12 mb-3 pl-0 pr-0 pr-md-2"
            label="CVC"
            onStateChange={onCardStateChange}
            variant="cvc"
            required
          />

          <CreditCardInput
            className="col-md-6 col-12 mb-3 pr-0 pl-0 pl-md-2"
            label={t("features.creditCardForm.expirationDateLabel")}
            onStateChange={onCardStateChange}
            variant="expiry"
            required
          />
        </div>

        <Divider className="mt-4 mb-3">{t("common.forms.separatorLabel")}</Divider>

        <ExpressCheckoutElement
          options={{ paymentMethods: { googlePay: "always", applePay: "always" } }}
          onConfirm={onConfirm}
        />

        {error && <MessageLayout className="w-100 mt-4" labelSection={error} color="error" />}
      </BoxLayout>

      {isExpiredSessionModalOpen && <ExpiredSessionModal onRefresh={onRefreshSession} isRefreshing={isRefreshing} />}
    </>
  );
};

CreditCardForm.defaultProps = {
  error: null,
  // defaultCard: null,
  isRefreshing: false,
};

CreditCardForm.propTypes = {
  name: PropTypes.string.isRequired,
  // cards: PropTypes.arrayOf({
  //   id: PropTypes.string.isRequired,
  //   brand: PropTypes.string.isRequired,
  //   last4: PropTypes.string.isRequired,
  //   exp_month: PropTypes.number.isRequired,
  //   exp_year: PropTypes.number.isRequired,
  // }).isRequired,
  onSelect: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onRefreshSession: PropTypes.func.isRequired,
  // defaultCard: PropTypes.string,
  error: PropTypes.string,
  isRefreshing: PropTypes.bool,
};

export default CreditCardForm;
